(() => {
  const ROOT_SELECTOR =
    '[data-goodlot-subscription]';

  const STORAGE_KEY =
    'goodlot_subscription_popup_v1';

  const TWO_DAYS_MS =
    2 * 24 * 60 * 60 * 1000;

  const TRIGGER_SCROLL_PERCENT =
    30;

  const KLAVIYO_REVISION =
    '2026-07-15';


  /* ========================================================
     STORAGE
     ======================================================== */

  function safeParse(
    value,
    fallback
  ) {
    try {
      return value
        ? JSON.parse(value)
        : fallback;
    } catch (_) {
      return fallback;
    }
  }


  function readState() {
    try {
      return safeParse(
        window.localStorage.getItem(
          STORAGE_KEY
        ),
        {}
      );
    } catch (_) {
      return {};
    }
  }


  function writeState(state) {
    try {
      window.localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify(state)
      );
    } catch (_) {}
  }


  /*
   * Keep the old stage/path values temporarily
   * so visitors from the previous two-exposure
   * version can be migrated safely.
   */
  function normalizedState(
    rawState = {}
  ) {
    return {
      subscribed:
        Boolean(
          rawState.subscribed
        ),

      cooldownUntil:
        Number(
          rawState.cooldownUntil ||
          0
        ),

      stage:
        Number(
          rawState.stage ||
          0
        ),

      firstShownPath:
        String(
          rawState.firstShownPath ||
          ''
        ),
    };
  }


  /* ========================================================
     TODAY'S DROP CAROUSEL
     ======================================================== */

  function initGoodLotDropCarousel(
    root
  ) {
    const viewport =
      root.querySelector(
        '[data-goodlot-carousel]'
      );

    const track =
      root.querySelector(
        '[data-goodlot-carousel-track]'
      );

    const prev =
      root.querySelector(
        '[data-goodlot-carousel-prev]'
      );

    const next =
      root.querySelector(
        '[data-goodlot-carousel-next]'
      );


    if (
      !viewport ||
      !track
    ) {
      return null;
    }


    const cards =
      Array.from(
        track.querySelectorAll(
          '.goodlot-subscription__product-card'
        )
      );


    if (!cards.length) {
      return null;
    }


    const prefersReducedMotion =
      window.matchMedia?.(
        '(prefers-reduced-motion: reduce)'
      ).matches;


    let scrollTicking =
      false;

    let dragging =
      false;

    let dragStartX =
      0;

    let dragStartScrollLeft =
      0;

    let activePointerId =
      null;


    const maxScrollLeft =
      () =>
        Math.max(
          0,
          viewport.scrollWidth -
            viewport.clientWidth
        );


    const update =
      () => {
        const max =
          maxScrollLeft();

        const tolerance =
          3;


        if (prev) {
          prev.disabled =
            viewport.scrollLeft <=
            tolerance;
        }


        if (next) {
          next.disabled =
            viewport.scrollLeft >=
            max -
              tolerance;
        }
      };


    const cardStep =
      () => {
        const firstCard =
          cards[0];


        if (!firstCard) {
          return (
            viewport.clientWidth *
            0.91
          );
        }


        const cardWidth =
          firstCard
            .getBoundingClientRect()
            .width;


        const styles =
          window.getComputedStyle(
            track
          );


        const gap =
          parseFloat(
            styles.columnGap ||
            styles.gap ||
            '0'
          ) || 0;


        return (
          cardWidth +
          gap
        );
      };


    const move =
      (direction) => {
        viewport.scrollBy({
          left:
            cardStep() *
            direction,

          behavior:
            prefersReducedMotion
              ? 'auto'
              : 'smooth',
        });
      };


    prev?.addEventListener(
      'click',
      () => move(-1)
    );


    next?.addEventListener(
      'click',
      () => move(1)
    );


    viewport.addEventListener(
      'scroll',
      () => {
        if (
          scrollTicking
        ) {
          return;
        }


        scrollTicking =
          true;


        requestAnimationFrame(
          () => {
            scrollTicking =
              false;

            update();
          }
        );
      },
      {
        passive: true,
      }
    );


    viewport.addEventListener(
      'keydown',
      (event) => {
        if (
          event.key ===
          'ArrowLeft'
        ) {
          event.preventDefault();

          move(-1);
        }


        if (
          event.key ===
          'ArrowRight'
        ) {
          event.preventDefault();

          move(1);
        }
      }
    );


    viewport.addEventListener(
      'pointerdown',
      (event) => {
        if (
          event.pointerType !==
            'mouse' ||
          event.button !==
            0
        ) {
          return;
        }


        dragging =
          true;

        dragStartX =
          event.clientX;

        dragStartScrollLeft =
          viewport.scrollLeft;

        activePointerId =
          event.pointerId;


        viewport.classList.add(
          'is-dragging'
        );


        viewport
          .setPointerCapture?.(
            event.pointerId
          );
      }
    );


    viewport.addEventListener(
      'pointermove',
      (event) => {
        if (
          !dragging ||
          event.pointerId !==
            activePointerId
        ) {
          return;
        }


        viewport.scrollLeft =
          dragStartScrollLeft -
          (
            event.clientX -
            dragStartX
          );
      }
    );


    const endDrag =
      (event) => {
        if (!dragging) {
          return;
        }


        if (
          activePointerId !==
            null &&
          event?.pointerId !==
            undefined &&
          event.pointerId !==
            activePointerId
        ) {
          return;
        }


        dragging =
          false;


        viewport.classList.remove(
          'is-dragging'
        );


        if (
          activePointerId !==
            null &&
          viewport
            .hasPointerCapture?.(
              activePointerId
            )
        ) {
          viewport
            .releasePointerCapture(
              activePointerId
            );
        }


        activePointerId =
          null;


        requestAnimationFrame(
          update
        );
      };


    viewport.addEventListener(
      'pointerup',
      endDrag
    );

    viewport.addEventListener(
      'pointercancel',
      endDrag
    );

    viewport.addEventListener(
      'lostpointercapture',
      endDrag
    );


    window.addEventListener(
      'resize',
      () =>
        requestAnimationFrame(
          update
        ),
      {
        passive: true,
      }
    );


    requestAnimationFrame(
      update
    );


    return {
      update,
    };
  }


  /* ========================================================
     SUBSCRIPTION POPUP
     ======================================================== */

  function initGoodLotSubscription(
    root
  ) {
    if (
      !root ||
      root.dataset
        .goodlotSubscriptionReady ===
        'true'
    ) {
      return;
    }


    root.dataset
      .goodlotSubscriptionReady =
      'true';


    /*
     * Render as a true page-level modal.
     *
     * Shopify sections can create stacking
     * contexts / overflow containers.
     * Moving the modal to body prevents
     * clipping.
     */
    const existingPortal =
      document.body.querySelector(
        `${ROOT_SELECTOR}[data-goodlot-popup-portal="true"]`
      );


    if (
      existingPortal &&
      existingPortal !==
        root
    ) {
      existingPortal.remove();
    }


    root.dataset
      .goodlotPopupPortal =
      'true';


    if (
      root.parentElement !==
      document.body
    ) {
      document.body.appendChild(
        root
      );
    }


    const panel =
      root.querySelector(
        '[data-goodlot-subscription-panel]'
      );


    const closeButtons =
      root.querySelectorAll(
        '[data-goodlot-subscription-close]'
      );


    const form =
      root.querySelector(
        '[data-goodlot-klaviyo-form]'
      );


    const email =
      root.querySelector(
        '[data-goodlot-subscription-email]'
      );


    const submit =
      root.querySelector(
        '[data-goodlot-subscription-submit]'
      );


    const error =
      root.querySelector(
        '[data-goodlot-subscription-error]'
      );


    const successAction =
      root.querySelector(
        '[data-goodlot-success-action]'
      );


    const carouselController =
      initGoodLotDropCarousel(
        root
      );


    if (
      !panel ||
      !form ||
      !email ||
      !submit
    ) {
      return;
    }


    const klaviyoPublicKey =
      String(
        root.dataset
          .klaviyoPublicKey ||
          ''
      ).trim();


    const klaviyoListId =
      String(
        root.dataset
          .klaviyoListId ||
          ''
      ).trim();


    let state =
      normalizedState(
        readState()
      );


    /* ======================================================
       OLD VERSION MIGRATION
       ====================================================== */

    /*
     * Previous version:
     *
     * stage === 1 meant the visitor had
     * already seen the first popup but was
     * still eligible for another exposure
     * on another page.
     *
     * New version:
     * one exposure only.
     *
     * Give those existing visitors a full
     * 2-day cooldown so they do not suddenly
     * receive another popup after deployment.
     */
    if (
      !state.subscribed &&
      state.stage === 1 &&
      !state.cooldownUntil
    ) {
      state.cooldownUntil =
        Date.now() +
        TWO_DAYS_MS;

      state.stage =
        0;

      state.firstShownPath =
        '';

      writeState(
        state
      );
    }


    /* ======================================================
       TEST MODE
       ====================================================== */

    const popupTestMode =
      new URLSearchParams(
        window.location.search
      ).get(
        'popup_test'
      ) === '1';


    /*
     * Test mode uses a temporary clean state.
     * It never writes a 2-day cooldown.
     */
    if (
      popupTestMode
    ) {
      state =
        normalizedState(
          {}
        );
    }


    /* ======================================================
       RUNTIME STATE
       ====================================================== */

    let popupOpen =
      false;

    let submitBusy =
      false;

    let lastFocusedElement =
      null;

    let pageScrollLocked =
      false;


    /* ======================================================
       PAGE SCROLL LOCK
       ====================================================== */

    const lockPageScroll =
      () => {
        if (
          pageScrollLocked
        ) {
          return;
        }


        pageScrollLocked =
          true;


        document
          .documentElement
          .classList.add(
            'goodlot-subscription-scroll-locked'
          );


        document
          .body
          .classList.add(
            'goodlot-subscription-scroll-locked'
          );
      };


    const unlockPageScroll =
      () => {
        if (
          !pageScrollLocked
        ) {
          return;
        }


        pageScrollLocked =
          false;


        document
          .documentElement
          .classList.remove(
            'goodlot-subscription-scroll-locked'
          );


        document
          .body
          .classList.remove(
            'goodlot-subscription-scroll-locked'
          );
      };


    /* ======================================================
       COOLDOWN
       ====================================================== */

    const resetCooldownIfExpired =
      () => {
        if (
          state.cooldownUntil &&
          Date.now() >=
            state.cooldownUntil
        ) {
          state.cooldownUntil =
            0;

          state.stage =
            0;

          state.firstShownPath =
            '';

          writeState(
            state
          );
        }
      };


    resetCooldownIfExpired();


    /* ======================================================
       SCROLL DETECTION
       ====================================================== */

    let activeScrollContainer =
      null;


    const isScrollableElement =
      (element) => {
        return Boolean(
          element &&
          element instanceof
            HTMLElement &&
          element.scrollHeight >
            element.clientHeight +
              4
        );
      };


    const scrollPercent =
      () => {
        /*
         * Some Shopify desktop themes
         * scroll an internal page wrapper
         * rather than window/document.
         */
        if (
          activeScrollContainer &&
          isScrollableElement(
            activeScrollContainer
          )
        ) {
          const max =
            activeScrollContainer
              .scrollHeight -
            activeScrollContainer
              .clientHeight;


          if (max > 0) {
            return Math.min(
              100,
              Math.max(
                0,
                (
                  activeScrollContainer
                    .scrollTop /
                  max
                ) *
                  100
              )
            );
          }
        }


        const scrollingElement =
          document.scrollingElement ||
          document.documentElement;


        const scrollTop =
          Math.max(
            window.scrollY ||
              0,

            scrollingElement
              .scrollTop ||
              0,

            document
              .documentElement
              .scrollTop ||
              0,

            document.body
              .scrollTop ||
              0
          );


        const scrollHeight =
          Math.max(
            scrollingElement
              .scrollHeight ||
              0,

            document
              .documentElement
              .scrollHeight ||
              0,

            document.body
              .scrollHeight ||
              0
          );


        const viewportHeight =
          Math.max(
            window.innerHeight ||
              0,

            document
              .documentElement
              .clientHeight ||
              0
          );


        const maxScroll =
          Math.max(
            0,
            scrollHeight -
              viewportHeight
          );


        /*
         * Preserve the previous behavior.
         */
        if (
          maxScroll <= 0
        ) {
          return 100;
        }


        return Math.min(
          100,
          Math.max(
            0,
            (
              scrollTop /
              maxScroll
            ) *
              100
          )
        );
      };


    /* ======================================================
       SHOULD POPUP SHOW?
       ====================================================== */

    const canShow =
      () => {
        resetCooldownIfExpired();


        /*
         * Debug:
         * open immediately regardless
         * of cooldown/subscription.
         */
        if (
          popupTestMode
        ) {
          return !popupOpen;
        }


        /*
         * Successful subscriber:
         * never display again.
         */
        if (
          state.subscribed
        ) {
          return false;
        }


        /*
         * Already open.
         */
        if (
          popupOpen
        ) {
          return false;
        }


        /*
         * Popup has already been shown
         * during the last 2 days.
         */
        if (
          Date.now() <
          state.cooldownUntil
        ) {
          return false;
        }


        /*
         * Original trigger remains:
         * 30% scroll.
         */
        return (
          scrollPercent() >=
          TRIGGER_SCROLL_PERCENT
        );
      };


    /* ======================================================
       OPEN POPUP
       ====================================================== */

    const openPopup =
      () => {
        if (
          !canShow()
        ) {
          return;
        }


        lastFocusedElement =
          document.activeElement
            instanceof HTMLElement
            ? document.activeElement
            : null;


        /*
         * IMPORTANT:
         *
         * An impression counts as soon as
         * the popup becomes visible.
         *
         * If the visitor does NOT subscribe,
         * they will not see the popup again
         * for 2 days.
         *
         * Test mode never modifies storage.
         */
        if (
          !popupTestMode
        ) {
          state.cooldownUntil =
            Date.now() +
            TWO_DAYS_MS;

          /*
           * Clear fields left from
           * the old two-exposure system.
           */
          state.stage =
            0;

          state.firstShownPath =
            '';

          writeState(
            state
          );
        }


        popupOpen =
          true;


        root.classList.remove(
          'is-inline-success'
        );


        root.classList.add(
          'is-open'
        );


        root.setAttribute(
          'aria-hidden',
          'false'
        );


        lockPageScroll();


        requestAnimationFrame(
          () => {
            carouselController
              ?.update();
          }
        );


        /*
         * Desktop only.
         *
         * Avoid autofocus on
         * iPhone / mobile Safari.
         */
        if (
          window.matchMedia(
            '(min-width: 750px)'
          ).matches
        ) {
          window.setTimeout(
            () => {
              email.focus({
                preventScroll:
                  true,
              });
            },
            360
          );
        }
      };


    /* ======================================================
       CLOSE POPUP
       ====================================================== */

    const closePopup =
      ({
        restoreFocus = true,
      } = {}) => {
        if (
          !popupOpen
        ) {
          return;
        }


        popupOpen =
          false;


        root.classList.remove(
          'is-open'
        );


        root.setAttribute(
          'aria-hidden',
          'true'
        );


        unlockPageScroll();


        if (
          restoreFocus &&
          lastFocusedElement &&
          document.contains(
            lastFocusedElement
          )
        ) {
          lastFocusedElement.focus({
            preventScroll:
              true,
          });
        }
      };


    /* ======================================================
       OPEN CHECK
       ====================================================== */

    const maybeOpen =
      () => {
        if (
          canShow()
        ) {
          openPopup();
        }
      };


    /* ======================================================
       SCROLL EVENT
       ====================================================== */

    let scrollTicking =
      false;


    const handleScroll =
      (event) => {
        const target =
          event?.target;


        if (
          target instanceof
            HTMLElement &&
          target !==
            document.body &&
          target !==
            document.documentElement &&
          isScrollableElement(
            target
          )
        ) {
          activeScrollContainer =
            target;
        }


        if (
          scrollTicking
        ) {
          return;
        }


        scrollTicking =
          true;


        requestAnimationFrame(
          () => {
            scrollTicking =
              false;

            maybeOpen();
          }
        );
      };


    /* ======================================================
       CLOSE BUTTONS
       ====================================================== */

    closeButtons.forEach(
      (button) => {
        button.addEventListener(
          'click',
          () =>
            closePopup()
        );
      }
    );


    /* ======================================================
       ESC KEY
       ====================================================== */

    document.addEventListener(
      'keydown',
      (event) => {
        if (
          popupOpen &&
          event.key ===
            'Escape'
        ) {
          event.preventDefault();

          closePopup();
        }
      }
    );


    /* ======================================================
       IOS BACKGROUND SCROLL PROTECTION
       ====================================================== */

    root.addEventListener(
      'touchmove',
      (event) => {
        if (
          !popupOpen
        ) {
          return;
        }


        if (
          panel.contains(
            event.target
          )
        ) {
          return;
        }


        event.preventDefault();
      },
      {
        passive: false,
      }
    );


    /* ======================================================
       SCROLL LISTENERS
       ====================================================== */

    window.addEventListener(
      'scroll',
      handleScroll,
      {
        passive: true,
      }
    );


    /*
     * Scroll does not bubble normally.
     *
     * Capture phase lets us detect Shopify
     * desktop scroll wrappers.
     */
    document.addEventListener(
      'scroll',
      handleScroll,
      {
        passive: true,
        capture: true,
      }
    );


    /*
     * Preserve previous responsive behavior.
     */
    window.addEventListener(
      'resize',
      maybeOpen,
      {
        passive: true,
      }
    );


    /*
     * Support bfcache / browser back-forward
     * restoration.
     */
    window.addEventListener(
      'pageshow',
      () => {
        requestAnimationFrame(
          maybeOpen
        );
      },
      {
        passive: true,
      }
    );


    /* ======================================================
       INLINE SUCCESS
       ====================================================== */

    const showInlineSuccess =
      () => {
        /*
         * User successfully subscribed.
         *
         * From this point forward,
         * popup is permanently disabled
         * in this browser.
         */
        state.subscribed =
          true;

        state.cooldownUntil =
          0;

        state.stage =
          0;

        state.firstShownPath =
          '';


        writeState(
          state
        );


        popupOpen =
          true;


        root.classList.add(
          'is-open'
        );


        root.classList.remove(
          'is-inline-success'
        );


        /*
         * Force a reflow so the success
         * animation starts from frame zero.
         */
        void root.offsetWidth;


        root.classList.add(
          'is-inline-success'
        );


        root.setAttribute(
          'aria-hidden',
          'false'
        );


        lockPageScroll();


        window.setTimeout(
          () => {
            successAction?.focus({
              preventScroll:
                true,
            });
          },
          220
        );
      };


    /* ======================================================
       KLAVIYO REQUEST
       ====================================================== */

    const subscribeWithKlaviyo =
      async (
        emailAddress
      ) => {
        if (
          !klaviyoPublicKey ||
          !klaviyoListId
        ) {
          throw new Error(
            'Missing Klaviyo public key or list ID.'
          );
        }


        const endpoint =
          `https://a.klaviyo.com/client/subscriptions?company_id=${encodeURIComponent(
            klaviyoPublicKey
          )}`;


        const payload = {
          data: {
            type:
              'subscription',

            attributes: {
              custom_source:
                "GoodLot Smart Savings - Today's drops popup",

              profile: {
                data: {
                  type:
                    'profile',

                  attributes: {
                    email:
                      emailAddress,

                    subscriptions: {
                      email: {
                        marketing: {
                          consent:
                            'SUBSCRIBED',
                        },
                      },
                    },
                  },
                },
              },
            },

            relationships: {
              list: {
                data: {
                  type:
                    'list',

                  id:
                    klaviyoListId,
                },
              },
            },
          },
        };


        const response =
          await fetch(
            endpoint,
            {
              method:
                'POST',

              mode:
                'cors',

              credentials:
                'omit',

              headers: {
                Accept:
                  'application/vnd.api+json',

                'Content-Type':
                  'application/vnd.api+json',

                revision:
                  KLAVIYO_REVISION,
              },

              body:
                JSON.stringify(
                  payload
                ),
            }
          );


        if (
          !response.ok
        ) {
          let responseBody =
            null;


          try {
            responseBody =
              await response.json();
          } catch (_) {}


          console.error(
            'GoodLot Klaviyo signup failed.',
            {
              status:
                response.status,

              response:
                responseBody,
            }
          );


          throw new Error(
            `Klaviyo subscription failed: ${response.status}`
          );
        }
      };


    /* ======================================================
       FORM SUBMIT
       ====================================================== */

    form.addEventListener(
      'submit',
      async (
        event
      ) => {
        event.preventDefault();


        if (
          submitBusy
        ) {
          return;
        }


        if (
          !form.reportValidity()
        ) {
          return;
        }


        const emailAddress =
          email.value
            .trim()
            .toLowerCase();


        submitBusy =
          true;


        submit.disabled =
          true;


        if (
          error
        ) {
          error.hidden =
            true;
        }


        const originalButtonText =
          submit.textContent ||
          'Keep me posted';


        submit.textContent =
          'Joining…';


        try {
          await subscribeWithKlaviyo(
            emailAddress
          );


          showInlineSuccess();

        } catch (
          requestError
        ) {
          console.error(
            'GoodLot subscription signup failed.',
            requestError
          );


          if (
            error
          ) {
            error.textContent =
              'Something went wrong. Please try again.';

            error.hidden =
              false;
          }

        } finally {
          submitBusy =
            false;


          submit.disabled =
            false;


          submit.textContent =
            originalButtonText;
        }
      }
    );


    /* ======================================================
       INITIAL CHECK
       ====================================================== */

    /*
     * Already subscribed:
     * do not initialize any popup opening.
     */
    if (
      state.subscribed &&
      !popupTestMode
    ) {
      return;
    }


    /*
     * If the visitor loads/reloads a PDP already
     * beyond the 30% point, this preserves the
     * existing behavior and evaluates eligibility.
     *
     * During cooldown canShow() simply returns false.
     */
    requestAnimationFrame(
      maybeOpen
    );
  }


  /* ========================================================
     INITIALIZE ALL
     ======================================================== */

  function initAll(
    scope = document
  ) {
    scope
      .querySelectorAll(
        ROOT_SELECTOR
      )
      .forEach(
        (root) => {
          initGoodLotSubscription(
            root
          );
        }
      );
  }


  if (
    document.readyState ===
    'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      () =>
        initAll(
          document
        ),
      {
        once: true,
      }
    );
  } else {
    initAll(
      document
    );
  }


  /*
   * Keep Shopify theme editor support.
   */
  document.addEventListener(
    'shopify:section:load',
    (event) => {
      initAll(
        event.target
      );
    }
  );
})();